import React, { useContext, useState, useCallback, useEffect } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { Button } from '@sumologic/yama-ds-components';

import { Formik } from 'formik';
import * as Yup from 'yup';

import Login from '~/models/Login';
import GeneralSettings from '~/models/GeneralSettings';

import types from '~/modules/customFieldTypes';
import { translate } from '~/modules/helpers/lang';
import { toErrorMessage } from '~/modules/helpers/error';

import Loader from '~/components/Loader/Loader';
import Translate from '~/components/Translate/Translate';

import FormField from '~/containers/FormField/FormField';
import StickyAlertContext from '~/containers/StickyAlertManager/StickyAlertContext';

import {
    getUserFields,
    requiredFieldMessage
} from '../GeneralSettings/UserSettings/userFields';

/**
 * Force change password page
 * @return {*}
 * @constructor
 * @author FM
 */
export default function ForceChangePassword({}) {
    const [userFields, setUserFields] = useState(null);

    useEffect(() => {
        GeneralSettings.getPasswordPolicy().then(response => {
            const passPolicy = response.data;
            let userFields = [];
            if (passPolicy.password_policy_enabled) {
                userFields = getUserFields(
                    passPolicy.min_characters,
                    passPolicy.min_uppercase,
                    passPolicy.min_lowercase,
                    passPolicy.min_digits_symbols,
                    true
                );
            } else {
                userFields = getUserFields();
            }
            setUserFields(userFields);
        });
    }, []);

    const getUserField = fieldId =>
        userFields?.find(field => field.id === fieldId);

    const passwordFieldTemplate = getUserField('password');
    const confirmPasswordFieldTemplate = getUserField('confirm_password');
    const passwordFields = [
        {
            id: 'current_user_password',
            name: 'current_user_password',
            label: translate('Current user password'),
            type: types.PASSWORD,
            required: true,
            validation: Yup.string().required(requiredFieldMessage)
        },
        {
            ...passwordFieldTemplate,
            label: translate('New password')
        },
        {
            ...confirmPasswordFieldTemplate,
            label: translate('New password confirmation')
        }
    ];

    const createFieldValidationSchema = useCallback(() => {
        const schema = passwordFields.reduce((schema, field) => {
            if (field.validation) schema[field.id] = field.validation;
            return schema;
        }, {});
        return Yup.object().shape(schema);
    }, [passwordFields]);
    const fieldValidationSchema = createFieldValidationSchema();

    /** FM: waiting change password */
    const [isChangeLoading, setIsChangeLoading] = useState(false);
    const stickyAlertContext = useContext(StickyAlertContext);

    /**
     * Show sticky login error
     * @param message
     * @author FM
     */
    const showStickyMessage = message => {
        stickyAlertContext.addStickyAlert({
            title: <Translate message="Force change password" />,
            status: 'danger',
            message: message
        });
    };

    return (
        <div id={'force-change-password'} className={'force-change-password'}>
            <div id={'login-page'}>
                <div className={'change-password-container'}>
                    <div className={'header'}>
                        <span className="section-title">
                            <span className={'title'}>
                                <Translate message={'Change password'} />
                            </span>
                        </span>
                        <span className="sub-section-title">
                            <span className={'title'}>
                                <Translate
                                    message={
                                        'Please change your password before proceeding'
                                    }
                                />
                            </span>
                        </span>
                    </div>
                    {!userFields && <Loader />}
                    {userFields && (
                        <Formik
                            validationSchema={fieldValidationSchema}
                            initialValues={{}}
                            isInitialValid={false}
                        >
                            {({ ...formProps }) => {
                                return (
                                    <>
                                        {passwordFields.map(field => {
                                            const {
                                                id,
                                                required,
                                                label,
                                                type
                                            } = field;
                                            let { hint } = field;
                                            const value = formProps.values[id];
                                            const isTouched =
                                                !!formProps.touched[id];
                                            const errors = formProps.errors[id];
                                            const onChange = (id, value) => {
                                                formProps.handleChange({
                                                    target: {
                                                        id,
                                                        value
                                                    }
                                                });
                                            };

                                            return (
                                                <Form.Group
                                                    key={id}
                                                    className={'form-group'}
                                                >
                                                    <Form.Label
                                                        required={required}
                                                        htmlFor={field.id}
                                                    >
                                                        {label}
                                                    </Form.Label>
                                                    <FormField
                                                        id={id}
                                                        label={label}
                                                        onChange={onChange}
                                                        onBlur={
                                                            formProps.handleBlur
                                                        }
                                                        type={type}
                                                        value={value}
                                                        isRequired={required}
                                                        isTouched={isTouched}
                                                        errors={errors}
                                                    />
                                                    {errors && (
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors}
                                                        </Form.Control.Feedback>
                                                    )}
                                                    {hint && (
                                                        <Form.Text className="text-muted">
                                                            {hint}
                                                        </Form.Text>
                                                    )}
                                                </Form.Group>
                                            );
                                        })}
                                        <div className={'buttons-container'}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                fullWidth
                                                disabled={!formProps.isValid}
                                                onClick={() => {
                                                    setIsChangeLoading(true);
                                                    Login.forceChangePassword(
                                                        formProps.values
                                                            .current_user_password,
                                                        formProps.values
                                                            .password
                                                    )
                                                        .then(() => {
                                                            window.location.href = `${APP_URL.django}/ui/`;
                                                        })
                                                        .catch(exception => {
                                                            console.error(
                                                                exception
                                                            );
                                                            showStickyMessage(
                                                                toErrorMessage(
                                                                    exception,
                                                                    'Change password error'
                                                                )
                                                            );
                                                        })
                                                        .then(() => {
                                                            setIsChangeLoading(
                                                                false
                                                            );
                                                        });
                                                }}
                                            >
                                                {isChangeLoading ? (
                                                    <Spinner
                                                        variant="dark"
                                                        animation="border"
                                                        size="sm"
                                                    />
                                                ) : (
                                                    <Translate message="Change password" />
                                                )}
                                            </Button>
                                        </div>
                                    </>
                                );
                            }}
                        </Formik>
                    )}
                </div>
            </div>
        </div>
    );
}
